<template>
  <div class="register-agents-container">
    <div class="header-row">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="$t('raBusiness.search.placeholder')"
          @input="filterBusinesses"
        />
        <i class="fas fa-search"></i>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      <p>{{ $t('raBusiness.loading') }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <i class="fas fa-exclamation-circle"></i>
      <p>{{ error }}</p>
      <button @click="fetchBusinesses" class="retry-btn">
        <i class="fas fa-redo"></i>
        {{ $t('common.retry') }}
      </button>
    </div>

    <!-- Data State -->
    <div v-else class="table-container">
      <table class="agents-table">
        <thead>
          <tr>
            <th>{{ $t('raBusiness.table.businessName') }}</th>
            <th>{{ $t('raBusiness.table.tradeName') }}</th>
            <th>{{ $t('raBusiness.table.state') }}</th>
            <th>{{ $t('raBusiness.table.status') }}</th>
            <th>{{ $t('raBusiness.table.ein') }}</th>
            <th>{{ $t('raBusiness.table.createdDate') }}</th>
            <th>{{ $t('raBusiness.table.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="business in paginatedBusinesses" :key="business.id">
            <td>{{ business.legalBusinessName }}</td>
            <td>{{ business.tradeName || '-' }}</td>
            <td>{{ business.state }}</td>
            <td>
              <span :class="['status-badge', business.status && business.status.toLowerCase()]">
                {{ business.status }}
              </span>
            </td>
            <td>{{ business.ein || $t('raBusiness.pending') }}</td>
            <td>{{ formatDate(business.createdAt) }}</td>
            <td>
              <button @click="viewBusiness(business.id)" class="action-btn view">
                <i class="fas fa-eye"></i>
                <span>{{ $t('raBusiness.actions.view') }}</span>
              </button>
            </td>
          </tr>
          <tr v-if="filteredBusinesses.length === 0">
            <td colspan="7" class="no-data">
              {{ searchQuery ? $t('raBusiness.noSearchResults') : $t('raBusiness.noBusinesses') }}
            </td>
          </tr>
        </tbody>
      </table>
      
      <!-- Pagination -->
      <div v-if="showPagination" class="pagination">
        <button 
          class="pagination-btn" 
          :disabled="currentPage === 1"
          @click="currentPage--"
          :aria-label="$t('common.previous')"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        
        <button 
          v-for="page in totalPages" 
          :key="page"
          class="pagination-btn"
          :class="{ active: currentPage === page }"
          @click="currentPage = page"
        >
          {{ page }}
        </button>
        
        <button 
          class="pagination-btn" 
          :disabled="currentPage === totalPages"
          @click="currentPage++"
          :aria-label="$t('common.next')"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import businessService from '@/services/businessService';

export default {
  name: 'RABusiness',
  props: {
    currentLanguage: {
      type: String,
      default: 'English'
    }
  },
  watch: {
    currentLanguage: {
      immediate: true,
      handler(newLanguage) {
        const lang = newLanguage === 'English' ? 'en' : 'es'
        this.$i18n.locale = lang
      }
    }
  },
  data() {
    return {
      businesses: [],
      searchQuery: '',
      filteredBusinesses: [],
      loading: false,
      error: null,
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    agentId() {
      return localStorage.getItem('agentId')
    },
    paginatedBusinesses() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredBusinesses.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredBusinesses.length / this.itemsPerPage);
    },
    showPagination() {
      return this.filteredBusinesses.length > this.itemsPerPage;
    }
  },
  methods: {
    async fetchBusinesses() {
      if (!localStorage.getItem('agentToken')) {
        console.error('No agent token found');
        this.$router.push('/agent/login');
        return;
      }

      this.loading = true;
      this.error = null;
      try {
        const agentId = localStorage.getItem('agentId');
        const response = await businessService.getAllBusinesses(agentId);
        if (response && response.success) {
          this.businesses = Array.isArray(response.data) ? response.data : [];
          this.filteredBusinesses = [...this.businesses];
        } else {
          this.businesses = [];
          this.filteredBusinesses = [];
          throw new Error(response.message || 'Failed to fetch businesses');
        }
      } catch (error) {
        console.error('Error fetching businesses:', error);
        this.error = error.message || 'Failed to load businesses';
        this.businesses = [];
        this.filteredBusinesses = [];
      } finally {
        this.loading = false;
      }
    },

    viewBusiness(id) {
      this.$router.push(`/ra/business/${id}`);
    },
    filterBusinesses() {
      const query = this.searchQuery.toLowerCase().trim();
      if (!query) {
        this.filteredBusinesses = this.businesses;
        return;
      }
      
      this.filteredBusinesses = this.businesses.filter(business => {
        if (!business) return false;
        return (
          (business.legalBusinessName && business.legalBusinessName.toLowerCase().includes(query)) ||
          (business.tradeName && business.tradeName.toLowerCase().includes(query)) ||
          (business.ein && business.ein.toLowerCase().includes(query)) ||
          (business.state && business.state.toLowerCase().includes(query)) ||
          (business.status && business.status.toLowerCase().includes(query)) ||
          (business.businessCategory && business.businessCategory.toLowerCase().includes(query)) ||
          (business.businessAddress && business.businessAddress.toLowerCase().includes(query))
        );
      });
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString(this.$i18n.locale === 'en' ? 'en-US' : 'es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  },
  created() {
    this.fetchBusinesses();
  }
}
</script>

<style scoped>
.register-agents-container {
  margin: 0 auto;
  padding: 2.0rem;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}
.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
}
.search-box {
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  padding: 0.2rem 1rem 0.2rem 0.8rem;
  margin-right: 1rem;
  width: 300px;
}
.search-box input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  flex: 1;
  padding: 0.6rem 0.5rem 0.6rem 0;
}
.search-box i {
  color: #888;
  font-size: 1.1rem;
  margin-left: 0.3rem;
}
.agents-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 0;
}
.agents-table th, .agents-table td {
  padding: 1rem 1.2rem;
  font-size: 1rem;
  text-align: left;
}
.agents-table th {
  background: #f5f5f5;
  font-weight: 700;
  color: #1a1a1a;
  border-bottom: 1.5px solid #e5e7eb;
}
.agents-table td {
  border-bottom: 1px solid #e5e7eb;
}
.agents-table tr:last-child td {
  border-bottom: none;
}
.status-badge {
  display: inline-block;
  padding: 0.3em 1em;
  border-radius: 1em;
  font-size: 0.95em;
  font-weight: 600;
  background: #e6f9ed;
  color: #059669;
}
.status-badge.draft {
  background: #f3f4f6;
  color: #6b7280;
}
.status-badge.pending {
  background: #fef3c7;
  color: #d97706;
}
.status-badge.suspended {
  background: #fee2e2;
  color: #dc2626;
}
.status-badge.terminated {
  background: #fee2e2;
  color: #dc2626;
}
.no-data {
  text-align: center;
  color: #888;
  font-size: 1rem;
  padding: 2rem 0;
}
.table-container {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  overflow: hidden;
}
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem 1rem 0.5rem;
  gap: 0.5rem;
  border-top: 1px solid #e5e7eb;
  margin-top: auto;
}
.pagination-btn {
  min-width: 40px;
  height: 40px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pagination-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
}
.pagination-btn.active {
  background: #006400;
  color: #fff;
  border-color: #006400;
}
.pagination-btn:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
  border-color: #e5e7eb;
}
.pagination-btn i {
  font-size: 0.75rem;
}
.loading-state, .error-state {
  text-align: center;
  padding: 3rem 1rem;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.loading-state i, .error-state i {
  font-size: 2rem;
  color: #006400;
  margin-bottom: 1rem;
}
.loading-state p, .error-state p {
  color: #374151;
  margin: 0.5rem 0;
}
.error-state i {
  color: #dc2626;
}
.retry-btn {
  margin-top: 1rem;
  background: #006400;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.retry-btn:hover {
  background: #005000;
}
</style>